import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";

import {
  RiskManagementMajorRiskBarChartsDocument,
  type RiskManagementMajorRiskBarChartsQuery,
  type RiskManagementMajorRiskBarChartsQueryVariables,
} from "$/__generated__/graphql";

import {
  appendRiskManagementSubgroups,
  convertOverallBarDataToSeries,
  filterNullishOPINTotals,
  filterNullishThreatTotals,
} from "./utils";
import { useGlobalFiltersContext } from "contexts/filters/global-filters/context";
import {
  type GeneralBarDatum,
  storageFormatters,
} from "@jana-technology/platform-ui";
import { TOOLTIP_SEMIBOLD } from "constants/styles";
import { usePublishedRiskUnitFormatter } from "../usePublishedRiskUnitFormatter";

export const useMajorRiskCharts = (
  options: useQuery.Options<
    RiskManagementMajorRiskBarChartsQuery,
    RiskManagementMajorRiskBarChartsQueryVariables
  >,
) => {
  const majorRiskChartsQueryResult = useQuery(
    RiskManagementMajorRiskBarChartsDocument,
    options,
  );
  const riskUnitFormatter = usePublishedRiskUnitFormatter();

  const {
    appliedWellName,
    appliedHierarchyName,
    majorEventOperationalRiskByWellName,
    majorEventInterventionRiskByWellName,
  } = useGlobalFiltersContext();

  const {
    majorRiskByThreatSeries,
    majorRiskByConsequenceSeries,
    majorRiskDataKeys,
  } = useMemo(() => {
    let majorRiskByThreatSeries: Array<GeneralBarDatum> = [];
    let majorRiskByConsequenceSeries: Array<GeneralBarDatum> = [];

    if (majorRiskChartsQueryResult.data?.riskManagementChartsForWellNode) {
      const { majorEventRiskByThreat, majorEventRiskByConsequence } =
        majorRiskChartsQueryResult.data.riskManagementChartsForWellNode;

      const tooltipTitle = appliedWellName
        ? `${appliedWellName} (${appliedHierarchyName})`
        : "";

      majorRiskByThreatSeries = convertOverallBarDataToSeries(
        filterNullishThreatTotals(majorEventRiskByThreat),
      );

      const filteredConsequenceSeries = filterNullishOPINTotals(
        majorEventRiskByConsequence,
      );

      majorRiskByConsequenceSeries = convertOverallBarDataToSeries(
        filteredConsequenceSeries,
      );

      for (let i = 0; i < majorRiskByThreatSeries.length; i++) {
        majorRiskByThreatSeries[i].title = tooltipTitle;
        majorRiskByThreatSeries[i].formatter = riskUnitFormatter;
        majorRiskByThreatSeries[i].tooltipExtras = [
          {
            label: "Risk Modality",
            value: "Operational Risk",
            subGroup: "Operational Risk",
            labelClassName: TOOLTIP_SEMIBOLD,
          },
          {
            label: "Risk Modality",
            value: "Intervention Risk",
            subGroup: "Intervention Risk",
            labelClassName: TOOLTIP_SEMIBOLD,
          },
          {
            label: "Threat LOF",
            value: storageFormatters.scientificNotation(
              majorEventRiskByThreat[i].opLof ?? 0,
            ),
            subGroup: "Operational Risk",
          },
          {
            label: "Threat LOF",
            value: storageFormatters.scientificNotation(
              majorEventRiskByThreat[i].inLof ?? 0,
            ),
            subGroup: "Intervention Risk",
          },
          {
            label: "Total Major Event Risk (Operational)",
            value: riskUnitFormatter(majorEventOperationalRiskByWellName),
            subGroup: "Operational Risk",
          },
          {
            label: "Total Major Event Risk (Intervention)",
            value: riskUnitFormatter(majorEventInterventionRiskByWellName),
            subGroup: "Intervention Risk",
          },
          {
            label: "Total Major Event Risk (Combined)",
            value: riskUnitFormatter(
              majorEventOperationalRiskByWellName +
                majorEventInterventionRiskByWellName,
            ),
          },
        ];
      }

      for (let i = 0; i < majorRiskByConsequenceSeries.length; i++) {
        majorRiskByConsequenceSeries[i].title = tooltipTitle;
        majorRiskByConsequenceSeries[i].formatter = riskUnitFormatter;

        appendRiskManagementSubgroups(
          [filteredConsequenceSeries[i]],
          majorRiskByConsequenceSeries[i],
          "By Category",
          riskUnitFormatter,
        );

        majorRiskByConsequenceSeries[i].tooltipExtras =
          majorRiskByConsequenceSeries[i].tooltipExtras?.concat([
            {
              label: "Risk Modality",
              value: "Operational Risk",
              subGroup: "Operational Risk",
              labelClassName: TOOLTIP_SEMIBOLD,
            },
            {
              label: "Risk Modality",
              value: "Intervention Risk",
              subGroup: "Intervention Risk",
              labelClassName: TOOLTIP_SEMIBOLD,
            },
            {
              label: "Total Major Event Risk (Operational)",
              value: riskUnitFormatter(majorEventOperationalRiskByWellName),
              subGroup: "Operational Risk",
            },
            {
              label: "Total Major Event Risk (Intervention)",
              value: riskUnitFormatter(majorEventInterventionRiskByWellName),
              subGroup: "Intervention Risk",
            },
            {
              label: "Total Major Event Risk (Combined)",
              value: riskUnitFormatter(
                majorEventOperationalRiskByWellName +
                  majorEventInterventionRiskByWellName,
              ),
            },
          ]);
      }

      return {
        majorRiskByThreatSeries,
        majorRiskByConsequenceSeries,
        majorRiskDataKeys: ["Operational Risk", "Intervention Risk"],
      };
    }

    return {
      majorRiskByThreatSeries: [],
      majorRiskByConsequenceSeries: [],
      majorRiskDataKeys: [],
    };
  }, [
    majorRiskChartsQueryResult.data?.riskManagementChartsForWellNode,
    majorEventOperationalRiskByWellName,
    majorEventInterventionRiskByWellName,
    appliedWellName,
    appliedHierarchyName,
    riskUnitFormatter,
  ]);

  return {
    majorRiskChartsQueryResult,
    majorRiskByThreatSeries,
    majorRiskByConsequenceSeries,
    majorRiskDataKeys,
  };
};
